import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Award, ArrowLeft, Loader2, Plus, Trash2, Move, Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RichTextEditor } from '@/components/ui/rich-text-editor/RichTextEditor';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { ImageUploader } from '@/components/ui/image-uploader';

// Form schema for validation
const formSchema = z.object({
  title: z.string().min(3, { message: "Title must be at least 3 characters" }),
  destination: z.string().optional(),
  description: z.string().optional(),
  best_season: z.string().optional(),
  duration: z.coerce.number().positive().optional().nullable(),
  featured_image: z.string().optional(),
  price: z.coerce.number().positive({ message: "Price must be a positive number" }),
  currency: z.string().default("USD"),
  is_featured: z.boolean().default(false),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  slug: z.string().optional()
});

// Define an interface for the itinerary data structure
interface PremiumItinerary {
  id: string;
  title: string;
  destination?: string;
  description?: string;
  best_season?: string;
  duration?: number;
  featured_image?: string;
  price: number;
  currency: string;
  is_featured: boolean;
  status: "draft" | "published" | "archived";
  slug: string;
  created_at?: string;
  updated_at?: string;
}

export default function EditPremiumItinerary() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [description, setDescription] = useState('');

  // Fetch itinerary data with proper typing
  const { data: itinerary, isLoading, isError } = useQuery({
    queryKey: ['premium-itinerary', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('premium_itineraries')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      return data as PremiumItinerary;
    },
    enabled: !!id, // Only run the query if the ID is available
  });

  // Initialize the form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      destination: "",
      description: "",
      best_season: "Year-round",
      duration: undefined,
      featured_image: "",
      price: 0,
      currency: "USD",
      is_featured: false,
      status: "draft",
      slug: ""
    },
    mode: "onChange"
  });

  useEffect(() => {
    if (itinerary) {
      form.reset({
        title: itinerary.title || "",
        destination: itinerary.destination || "",
        description: itinerary.description || "",
        best_season: itinerary.best_season || "Year-round",
        duration: itinerary.duration || undefined,
        featured_image: itinerary.featured_image || "",
        price: itinerary.price || 0,
        currency: itinerary.currency || "USD",
        is_featured: itinerary.is_featured || false,
        status: itinerary.status || "draft",
        slug: itinerary.slug || ""
      });
      setDescription(itinerary.description || '');
    }
  }, [itinerary, form]);

  // Generate a slug from the title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Handle form submission
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);

      // Generate a slug from the title
      const slug = generateSlug(values.title);

      // Prepare the data - ensure all required fields are included
      const itineraryData = {
        title: values.title, // Explicitly include title since it's required
        destination: values.destination,
        description,
        best_season: values.best_season,
        duration: values.duration,
        featured_image: values.featured_image,
        price: values.price,
        currency: values.currency,
        is_featured: values.is_featured,
        status: values.status,
        slug
      };

      // Update the premium itinerary
      const { error } = await supabase
        .from('premium_itineraries')
        .update(itineraryData)
        .eq('id', id);

      if (error) {
        throw error;
      }

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['premium-itineraries', id] });
      queryClient.invalidateQueries({ queryKey: ['premium-itineraries'] });

      toast.success("Premium itinerary updated successfully");

    } catch (error: any) {
      console.error("Error updating premium itinerary:", error);
      toast.error(error.message || "Failed to update premium itinerary");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle itinerary deletion
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = async () => {
    try {
      setIsDeleting(true);

      // Delete the itinerary
      const { error } = await supabase
        .from('premium_itineraries')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['premium-itineraries'] });

      toast.success("Premium itinerary deleted successfully");

      // Navigate back to the list of itineraries
      navigate('/dashboard/premium-itineraries');
    } catch (error: any) {
      console.error("Error deleting premium itinerary:", error);
      toast.error(error.message || "Failed to delete premium itinerary");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading itinerary...
      </div>
    );
  }

  if (isError || !itinerary) {
    return (
      <div className="flex items-center justify-center h-full">
        Error loading itinerary.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard/premium-itineraries')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Premium Itineraries
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Award className="h-6 w-6 text-primary" />
        <h1 className="text-3xl font-bold tracking-tight">Edit Premium Itinerary</h1>
        <Badge>{itinerary.status}</Badge>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter itinerary title" {...field} />
                    </FormControl>
                    <FormDescription>
                      A descriptive title for your premium itinerary
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="destination"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Destination</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Paris, France" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          placeholder="e.g. 7"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="best_season"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Best Season</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Summer, Year-round"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="featured_image"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Featured Image</FormLabel>
                      <FormControl>
                        <ImageUploader
                          onImageUploaded={(url) => field.onChange(url)}
                          currentImage={field.value}
                          label=""
                          aspectRatio={16 / 9}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div>
                <Label>Description</Label>
                <div className="mt-2">
                  <RichTextEditor
                    value={description}
                    onChange={setDescription}
                    placeholder="Enter a detailed description of this premium itinerary"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pricing & Availability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="e.g. 29.99"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="EUR">EUR (€)</SelectItem>
                          <SelectItem value="GBP">GBP (£)</SelectItem>
                          <SelectItem value="CAD">CAD (C$)</SelectItem>
                          <SelectItem value="AUD">AUD (A$)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              <div className="flex flex-col gap-6 sm:flex-row sm:justify-between">
                <FormField
                  control={form.control}
                  name="is_featured"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-0.5">
                        <FormLabel>Featured Itinerary</FormLabel>
                        <FormDescription>
                          Featured itineraries are highlighted on the homepage
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Only published itineraries are visible to users
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Itinerary
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the itinerary from our servers.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Confirm Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard/premium-itineraries')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Premium Itinerary'
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
